/**
 * Cart Drawer
 * Manages the slide-out cart drawer with AJAX cart functionality
 */

(function() {
  'use strict';

  const CartDrawer = {
    selectors: {
      drawer: '#CartDrawer',
      overlay: '[data-cart-drawer-close]',
      body: '[data-cart-drawer-body]',
      items: '[data-cart-items]',
      empty: '[data-cart-empty]',
      loading: '[data-cart-loading]',
      footer: '[data-cart-footer]',
      subtotal: '[data-cart-subtotal]',
      discounts: '[data-cart-discounts]',
      cartCount: '#CartCount, #MobileCartCount',
      trigger: '[data-cart-drawer-trigger]',
      productForm: 'form[action*="/cart/add"]'
    },

    init: function() {
      this.drawer = document.querySelector(this.selectors.drawer);
      if (!this.drawer) {
        console.warn('Cart drawer not found in DOM');
        return;
      }

      console.log('Cart drawer initialized');
      this.bindEvents();
      this.setupFocusTrap();
      this.listenForCartChanges();
    },

    bindEvents: function() {
      const self = this;

      // Open drawer triggers
      document.querySelectorAll(this.selectors.trigger).forEach(function(trigger) {
        trigger.addEventListener('click', function(e) {
          e.preventDefault();
          self.open();
        });
      });

      // Close drawer triggers
      document.querySelectorAll(this.selectors.overlay).forEach(function(closeBtn) {
        closeBtn.addEventListener('click', function(e) {
          e.preventDefault();
          self.close();
        });
      });

      // ESC key to close
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && self.isOpen()) {
          self.close();
        }
      });

      // Event delegation for cart item interactions
      this.drawer.addEventListener('click', function(e) {
        // Remove item
        if (e.target.closest('[data-cart-item-remove]')) {
          e.preventDefault();
          const item = e.target.closest('[data-cart-item]');
          const key = item.dataset.lineKey;
          self.removeItem(key);
        }

        // Decrease quantity
        if (e.target.closest('[data-cart-item-decrease]')) {
          e.preventDefault();
          const item = e.target.closest('[data-cart-item]');
          const input = item.querySelector('[data-cart-item-quantity]');
          const newQty = Math.max(0, parseInt(input.value, 10) - 1);
          self.updateQuantity(item.dataset.lineKey, newQty);
        }

        // Increase quantity
        if (e.target.closest('[data-cart-item-increase]')) {
          e.preventDefault();
          const item = e.target.closest('[data-cart-item]');
          const input = item.querySelector('[data-cart-item-quantity]');
          const newQty = parseInt(input.value, 10) + 1;
          self.updateQuantity(item.dataset.lineKey, newQty);
        }
      });

      // Manual quantity input change (debounced)
      this.drawer.addEventListener('change', function(e) {
        if (e.target.matches('[data-cart-item-quantity]')) {
          const item = e.target.closest('[data-cart-item]');
          const newQty = parseInt(e.target.value, 10) || 0;
          self.updateQuantity(item.dataset.lineKey, newQty);
        }
      });
    },

    listenForCartChanges: function() {
      const self = this;

      // Listen for jQuery AJAX events (from existing theme.js)
      $(document).on('ajaxSuccess', function(_event, _xhr, settings) {
        // Check if this was a cart add request
        if (settings.url && settings.url.indexOf('/cart/add.js') !== -1) {
          console.log('Item added to cart via AJAX, opening drawer');

          // Open drawer after a brief delay
          setTimeout(function() {
            self.open();
          }, 100);

          // Remove "View Cart" button and replace with message
          setTimeout(function() {
            const $viewCartBtn = $('.btn--view-cart');
            if ($viewCartBtn.length) {
              $viewCartBtn.remove();
              console.log('Removed View Cart button');
            }
          }, 50);
        }
      });

      // Intercept clicks on "View Cart" button and open drawer instead
      $(document).on('click', '.btn--view-cart', function(e) {
        e.preventDefault();
        e.stopPropagation();
        console.log('View Cart button clicked, opening drawer instead');
        self.open();
        return false;
      });

      // Also listen for our custom event (for future extensibility)
      document.addEventListener('cart:item-added', function() {
        console.log('cart:item-added event received');
        self.open();
      });
    },

    addToCart: function(formData) {
      const self = this;

      return fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
        .then(function(response) {
          if (!response.ok) {
            return response.json().then(function(data) {
              throw new Error(data.description || 'Failed to add to cart');
            });
          }
          return response.json();
        })
        .then(function(item) {
          // Update cart count immediately
          self.fetchCart();

          // Announce to screen readers
          self.announceUpdate('Item added to cart');

          // Dispatch custom event
          document.dispatchEvent(new CustomEvent('cart:item-added', {
            detail: { item: item }
          }));

          return item;
        });
    },

    open: function() {
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      this.fetchCart();

      // Focus management
      this.previousFocus = document.activeElement;
      setTimeout(() => {
        const closeBtn = this.drawer.querySelector('[data-cart-drawer-close]');
        if (closeBtn) closeBtn.focus();
      }, 100);
    },

    close: function() {
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');

      // Return focus
      if (this.previousFocus) {
        this.previousFocus.focus();
      }
    },

    isOpen: function() {
      return this.drawer.classList.contains('is-open');
    },

    setupFocusTrap: function() {
      const self = this;
      const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

      this.drawer.addEventListener('keydown', function(e) {
        if (e.key !== 'Tab' || !self.isOpen()) return;

        const focusable = self.drawer.querySelectorAll(focusableElements);
        const firstFocusable = focusable[0];
        const lastFocusable = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        } else if (!e.shiftKey && document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      });
    },

    fetchCart: function() {
      const self = this;
      this.showLoading();

      fetch('/cart.js')
        .then(function(response) {
          return response.json();
        })
        .then(function(cart) {
          self.renderCart(cart);
          self.updateCartCount(cart.item_count);
        })
        .catch(function(error) {
          console.error('Error fetching cart:', error);
          self.showError();
        });
    },

    updateQuantity: function(key, quantity) {
      const self = this;

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      })
        .then(function(response) {
          return response.json();
        })
        .then(function(cart) {
          self.renderCart(cart);
          self.updateCartCount(cart.item_count);
          self.announceUpdate('Cart updated');
        })
        .catch(function(error) {
          console.error('Error updating cart:', error);
          self.fetchCart(); // Refetch to reset state
        });
    },

    removeItem: function(key) {
      this.updateQuantity(key, 0);
    },

    renderCart: function(cart) {
      const itemsContainer = this.drawer.querySelector(this.selectors.items);
      const emptyState = this.drawer.querySelector(this.selectors.empty);
      const footer = this.drawer.querySelector(this.selectors.footer);
      const loading = this.drawer.querySelector(this.selectors.loading);

      loading.style.display = 'none';

      if (cart.item_count === 0) {
        // Show empty state
        itemsContainer.style.display = 'none';
        footer.style.display = 'none';
        emptyState.style.display = 'block';
      } else {
        // Show cart items
        emptyState.style.display = 'none';
        itemsContainer.style.display = 'block';
        footer.style.display = 'block';

        // Render items
        itemsContainer.innerHTML = this.renderItems(cart.items);

        // Update subtotal
        const subtotalEl = this.drawer.querySelector(this.selectors.subtotal);
        if (subtotalEl) {
          subtotalEl.textContent = this.formatMoney(cart.total_price);
        }

        // Render cart-level discounts
        this.renderDiscounts(cart);
      }
    },

    renderItems: function(items) {
      return items.map(function(item) {
        const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
          ? '<p class="cart-drawer-item__variant">' + item.variant_title + '</p>'
          : '';

        const originalLinePrice = item.original_line_price !== item.final_line_price
          ? '<span class="cart-drawer-item__line-price--original">' + CartDrawer.formatMoney(item.original_line_price) + '</span>'
          : '';

        const finalLinePrice = '<span class="cart-drawer-item__line-price' + (item.original_line_price !== item.final_line_price ? '--sale' : '') + '">'
          + CartDrawer.formatMoney(item.final_line_price) + '</span>';

        const imageHtml = item.image
          ? '<img src="' + item.image + '" alt="' + item.product_title + '" loading="lazy">'
          : '<div class="cart-drawer-item__no-image">No image</div>';

        return '<div class="cart-drawer-item" data-cart-item data-line-key="' + item.key + '">' +
          '<div class="cart-drawer-item__image">' +
            '<a href="' + item.url + '">' + imageHtml + '</a>' +
          '</div>' +
          '<div class="cart-drawer-item__details">' +
            '<div class="cart-drawer-item__header">' +
              '<a href="' + item.url + '" class="cart-drawer-item__title">' + item.product_title + '</a>' +
              '<button type="button" class="cart-drawer-item__remove" data-cart-item-remove aria-label="Remove ' + item.product_title + '">&times;</button>' +
            '</div>' +
            variantTitle +
            '<div class="cart-drawer-item__footer">' +
              '<div class="cart-drawer-item__line-price">' + originalLinePrice + finalLinePrice + '</div>' +
              '<div class="cart-drawer-item__quantity">' +
                '<button type="button" class="cart-drawer-item__qty-btn" data-cart-item-decrease aria-label="Decrease quantity">-</button>' +
                '<input type="number" class="cart-drawer-item__qty-input" value="' + item.quantity + '" min="0" data-cart-item-quantity aria-label="Quantity">' +
                '<button type="button" class="cart-drawer-item__qty-btn" data-cart-item-increase aria-label="Increase quantity">+</button>' +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>';
      }).join('');
    },

    renderDiscounts: function(cart) {
      const discountsContainer = this.drawer.querySelector(this.selectors.discounts);
      if (!discountsContainer) return;

      if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
        const discountsHtml = cart.cart_level_discount_applications.map(function(discount) {
          return '<div class="cart-drawer__discount">' +
            '<span class="cart-drawer__discount-title">' + discount.title + '</span>' +
            '<span class="cart-drawer__discount-amount">-' + CartDrawer.formatMoney(discount.total_allocated_amount) + '</span>' +
          '</div>';
        }).join('');
        discountsContainer.innerHTML = discountsHtml;
        discountsContainer.style.display = 'block';
      } else {
        discountsContainer.innerHTML = '';
        discountsContainer.style.display = 'none';
      }
    },

    updateCartCount: function(count) {
      const countElements = document.querySelectorAll(this.selectors.cartCount);
      countElements.forEach(function(el) {
        el.textContent = count;
      });

      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { itemCount: count }
      }));
    },

    showLoading: function() {
      const loading = this.drawer.querySelector(this.selectors.loading);
      const items = this.drawer.querySelector(this.selectors.items);
      const empty = this.drawer.querySelector(this.selectors.empty);
      const footer = this.drawer.querySelector(this.selectors.footer);

      loading.style.display = 'block';
      items.style.display = 'none';
      empty.style.display = 'none';
      footer.style.display = 'none';
    },

    showError: function() {
      const itemsContainer = this.drawer.querySelector(this.selectors.items);
      itemsContainer.innerHTML = '<p class="cart-drawer__error">Unable to load cart. Please refresh the page.</p>';
    },

    announceUpdate: function(message) {
      // Screen reader announcement
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'visually-hidden';
      announcement.textContent = message;
      document.body.appendChild(announcement);
      setTimeout(function() {
        document.body.removeChild(announcement);
      }, 1000);
    },

    formatMoney: function(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents);
      }
      // Fallback formatting
      return '$' + (cents / 100).toFixed(2);
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      CartDrawer.init();
    });
  } else {
    CartDrawer.init();
  }

  // Expose to global scope for external triggers
  window.CartDrawer = CartDrawer;

})();
